/**
 * Maps a backend modified-image `sizeName` to its ilox copy keys and its
 * placement in the two-card layout (matching the Figma / the iStock
 * DownloadKSizeSelector reference).
 *
 * The backend emits low/medium/high; ilox copy was authored under the
 * 1k/2k/4k naming, so both spellings (plus the legacy `super` alias) map
 * onto the same entry.
 *
 * `rank` orders the cards: Standard (0), Higher resolution (1), Enhanced
 * detail (2) — each in its own card. "Standard" (the native edit) has no
 * "what to expect" disclosure; the upscaled tiers each render two lines — the
 * `_ai` paragraph then the `_thumb` "Best for…" line.
 *
 * Returns null for an unknown sizeName so callers can fall back to raw data.
 */
export interface ModifiedSizeCopy {
  titleKey: string;
  descriptionKey: string;
  whatToExpectKeys: string[];
  rank: number;
}

const STANDARD: ModifiedSizeCopy = {
  titleKey: 'preshot_size_standard_image',
  descriptionKey: 'preshot_size_1k_description',
  whatToExpectKeys: [],
  rank: 0,
};

const HIGHER: ModifiedSizeCopy = {
  titleKey: 'preshot_size_higher_resolution',
  descriptionKey: 'preshot_size_4k_description',
  whatToExpectKeys: [
    'preshot_size_4k_what_to_expect_ai',
    'preshot_size_4k_what_to_expect_thumb',
  ],
  rank: 1,
};

const ENHANCED: ModifiedSizeCopy = {
  titleKey: 'preshot_size_enhanced_detail',
  descriptionKey: 'preshot_size_2k_description',
  whatToExpectKeys: [
    'preshot_size_2k_what_to_expect_ai',
    'preshot_size_2k_what_to_expect_thumb',
  ],
  rank: 2,
};

const BY_SIZE_NAME: Record<string, ModifiedSizeCopy> = {
  low: STANDARD,
  '1k': STANDARD,
  medium: ENHANCED,
  '2k': ENHANCED,
  high: HIGHER,
  super: HIGHER, // legacy alias
  '4k': HIGHER,
};

const modifiedSizeCopy = (sizeName: string): ModifiedSizeCopy | null =>
  BY_SIZE_NAME[sizeName] || null;

export default modifiedSizeCopy;
